import React, { useState, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { loadExtraOptions, type ExtraOption } from './dineOpsExtras';
import type { DineAddonGroup } from 'api/types';

/**
 * The add-on set dialog: the set's name, and its extras.
 *
 * Since DO-315 an extra is an item picked from the menu (usually the Extras
 * category), not a typed name and price: its name and price come from the
 * item, and each outlet charges its own price for it.
 */

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DineAddonGroup | null;
  onSave: () => void;
  onClose: () => void;
}

interface AddonRow {
  id?: string;
  addonItemId: string;
  displayOrder: number;
}

const DineOpsAddonGroupModal: React.FC<Props> = ({ isOpen, mode, record, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rows, setRows] = useState<AddonRow[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [rowVersion, setRowVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extraOptions, setExtraOptions] = useState<ExtraOption[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    loadExtraOptions().then(setExtraOptions).catch(() => setExtraOptions([]));

    if (mode === 'edit' && record) {
      setName(record.addonGroupName);
      setDescription(record.description ?? '');
      setIsActive(record.isActive);
      setGroupId(record.id);
      setRowVersion(record.rowVersion);
      setRows((record.addons ?? []).map((a, i) => ({
        id: a.id, addonItemId: a.addonItemId ?? '', displayOrder: a.displayOrder || (i + 1) * 10,
      })));
    } else {
      setName('');
      setDescription('');
      setIsActive(true);
      setGroupId(null);
      setRowVersion(0);
      setRows([]);
    }
  }, [isOpen, mode, record]);

  const save = useCallback(async () => {
    if (!name.trim()) {
      setError('An add-on set needs a name.');
      return;
    }

    const named = rows.filter((r) => r.addonItemId);
    if (named.length !== new Set(named.map((r) => r.addonItemId)).size) {
      setError('The same extra is picked twice.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      /*
       * Two calls, header then extras, because they are two endpoints. The
       * header is saved first: if the extras call then fails, the set exists
       * with no extras, which is visible and fixable. The other order would
       * leave extras with nowhere to attach.
       */
      let id = groupId;
      if (id) {
        const saved = await dineOpsService.updateAddonGroup(id, {
          addonGroupName: name.trim(),
          description: description.trim() || undefined,
          isActive,
          rowVersion,
        });
        setRowVersion(saved.rowVersion);
      } else {
        const created = await dineOpsService.createAddonGroup({
          addonGroupName: name.trim(),
          description: description.trim() || undefined,
        });
        id = created.id;
        setGroupId(created.id);
        setRowVersion(created.rowVersion);
      }

      await dineOpsService.saveAddons(
        id!,
        named.map((r, i) => ({
          id: r.id,
          addonItemId: r.addonItemId,
          displayOrder: r.displayOrder || (i + 1) * 10,
        })),
      );

      notificationService.success('Saved', `'${name.trim()}' saved with ${named.length} extra(s).`);
      onSave();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The add-on set could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [name, description, isActive, rows, groupId, rowVersion, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add an add-on set' : `Edit — ${name || 'set'}`}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={18} />
          </button>
        </div>

        <div className="modal-content" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {error && (
            <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>
          )}

          <div className="form-group">
            <label className="form-label">Set name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Curry extras"
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <input
              type="text"
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this set is for"
              maxLength={500}
            />
          </div>

          {mode === 'edit' && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span>Active</span>
              </label>
            </div>
          )}

          <div style={{ fontSize: 12, opacity: 0.75, margin: '14px 0 8px' }}>
            The extras in this set. Every menu group pointing at it offers these, unless a dish
            declares its own. Pick each from the menu (usually the Extras category); its name and
            price come from that item.
          </div>

          {rows.map((r, i) => (
            <div key={i} data-addon-row style={{ display: 'flex', gap: 8, alignItems: 'end', marginBottom: 8 }}>
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
                <SyutSelect
                  label="Extra"
                  options={extraOptions.filter((o) => o.value === r.addonItemId || !rows.some((x) => x.addonItemId === o.value))}
                  value={r.addonItemId}
                  onChange={(v) => setRows((p) => p.map((x, j) => (j === i ? { ...x, addonItemId: (v as string) || '' } : x)))}
                  placeholder="Pick an extra"
                  size="sm"
                  menuPortalTarget={document.body}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
                <button
                  className="btn btn-secondary"
                  style={{ marginBottom: 4 }}
                  onClick={() => setRows((p) => p.filter((_, j) => j !== i))}
                  title="Remove this extra"
                >
                  <IconSystem.trash size={14} />
                </button>
              </div>
            </div>
          ))}

          <button
            className="btn btn-secondary"
            onClick={() => setRows((p) => [...p, { addonItemId: '', displayOrder: (p.length + 1) * 10 }])}
          >
            <IconSystem.add size={14} /> Add an extra
          </button>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default DineOpsAddonGroupModal;
